import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Collapsible from 'Douleutaras/components/Collapsible/Collapsible';
import useIsHydrated from 'Douleutaras/hooks/useIsHydrated';

import ServiceRow from './ServiceRow';
import ServiceInfoButton from './ServiceInfoButton';
import ServiceInfoModal from './ServiceInfoModal';
import { CatalogueCategory, CatalogueService } from './types';
import './ServiceCategoryRow.scss';

interface ServiceCategoryRowProps {
  category: CatalogueCategory;
  /** Initial expand state. Defaults to collapsed (perf: sub2 render deferred until open). */
  defaultExpanded?: boolean;
  /** Invoked when a child service row is clicked — routes into the post-job (booking) flow. */
  onServiceClick?(service: CatalogueService): void;
  /** Invoked when the category is expanded. Collapsing does not call it. */
  onCategoryExpand?(category: CatalogueCategory): void;
  /** Invoked when the category header's ⓘ is clicked. Expanding does not call it. */
  onCategoryInfoClick?(category: CatalogueCategory): void;
  /** Invoked when a child service row's ⓘ is clicked. */
  onServiceInfoClick?(service: CatalogueService): void;
}

/**
 * A collapsible category built on the reusable {@link Collapsible}: tinted header with the
 * category label, a collapse chevron, and a ⓘ button (opens the info modal) whenever the
 * payload carries info copy for the category. Owns the modal state; the expand/collapse
 * state lives inside `Collapsible`.
 *
 * The category itself is NOT a booking target — only its child service rows are. Child rows
 * (sub1 + sub2) are only mounted while expanded, since `Collapsible` defers its body.
 */
export const ServiceCategoryRow = ({
  category,
  defaultExpanded = false,
  onServiceClick,
  onCategoryExpand,
  onCategoryInfoClick,
  onServiceInfoClick
}: ServiceCategoryRowProps) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const hydrated = useIsHydrated();
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Server-render (and the matching first client render) expanded, so the priced service rows
  // land in the crawlable HTML; after hydration fall back to the interactive default.
  const isExpanded = hydrated ? expanded : true;

  const handleToggle = (next: boolean) => {
    setExpanded(next);

    // Only opening counts as an interaction; a collapse reports nothing
    if (next && onCategoryExpand) {
      onCategoryExpand(category);
    }
  };

  const handleInfoClick = () => {
    setInfoOpen(true);

    if (onCategoryInfoClick) {
      onCategoryInfoClick(category);
    }
  };

  const info = category.info;

  return (
    <li styleName="service-category">
      <Collapsible
        id={`service-category-${category.categoryId}`}
        title={category.label}
        headerActions={info ? <ServiceInfoButton onClick={handleInfoClick} /> : undefined}
        expanded={isExpanded}
        onToggle={handleToggle}
      >
        <ul styleName="service-category__services">
          {category.services.map(service => (
            <ServiceRow
              key={service.serviceId}
              service={service}
              onServiceClick={onServiceClick}
              onInfoClick={onServiceInfoClick}
            />
          ))}
        </ul>
      </Collapsible>

      {info && (
        <ServiceInfoModal
          isOpen={infoOpen}
          title={category.label}
          layout="accordion"
          sections={[
            {
              heading: t('T.PROS-PROFILE.SERVICES.INFO-MODAL-CATEGORY', 'About the category'),
              html: info
            }
          ]}
          onClose={() => setInfoOpen(false)}
        />
      )}
    </li>
  );
};

export default ServiceCategoryRow;
